import { useEffect } from "react";
import { useStickToBottom } from "use-stick-to-bottom";

import Box from "ds/components/Box";
import usePrevious from "hooks/usePrevious";
import { IconComponent } from "types/Icon";

import { AiChatMessage } from "../types";
import AvatarAnimated from "../Avatar/Animated";
import { ToolPart } from "../Tool/helpers";
import AiChatMessagesItem from "./Item";
import styles from "./styles.module.css";

type AiChatMessagesProps = {
  messages: AiChatMessage[];
  isStreaming: boolean;
  toolIcons?: Record<string, IconComponent>;
  onApproveTool?: (part: ToolPart) => void;
  onRejectTool?: (part: ToolPart) => void;
  onReusePrompt?: (text: string) => void;
};

const AiChatMessages = ({
  messages,
  isStreaming,
  toolIcons,
  onApproveTool,
  onRejectTool,
  onReusePrompt,
}: AiChatMessagesProps) => {
  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom({
    initial: "instant",
    resize: "smooth",
  });

  const userMessagesCount = messages.filter((m) => m.role === "user").length;
  const prevUserMessagesCount = usePrevious(userMessagesCount);

  useEffect(() => {
    if (prevUserMessagesCount !== undefined && userMessagesCount > prevUserMessagesCount) {
      scrollToBottom();
    }
  }, [userMessagesCount, prevUserMessagesCount, scrollToBottom]);

  const lastMessage = messages.at(-1);

  return (
    <div ref={scrollRef} className={styles.scrollContainer}>
      <Box ref={contentRef} direction="column" gap="xx-large" padding="x-large">
        {messages.map((message) => {
          const isLast = message === lastMessage;

          return (
            <AiChatMessagesItem
              key={message.id}
              message={message}
              isLast={isLast}
              isStreaming={isStreaming && isLast}
              toolIcons={toolIcons}
              onApproveTool={onApproveTool}
              onRejectTool={onRejectTool}
              onReusePrompt={onReusePrompt}
            />
          );
        })}

        {isStreaming && lastMessage?.role === "user" && (
          <Box direction="column" gap="medium" align="start" className={styles.assistantMessage}>
            <AvatarAnimated />
          </Box>
        )}
      </Box>
    </div>
  );
};

export default AiChatMessages;
